<script setup lang="ts">
/**
 * Champ d'une carte du volet droit (groupe IA, observation) : libellé en
 * petites capitales, zone à droite du libellé (badge, compteur, indicateur)
 * et contenu. Habille à l'identique les champs Element Plus et le sélecteur
 * de partie sur le fond sombre de l'outil. L'accent (focus, survol) suit
 * `--card-accent`, posée par la carte — violet par défaut (analyse).
 */

defineProps<{
  label: string;
}>();
</script>

<template>
  <!-- click.stop : toucher un champ ne (dé)sélectionne pas la carte -->
  <div class="edit-field" @click.stop>
    <div class="edit-label-row">
      <span class="edit-label">{{ label }}</span>
      <slot name="aside" />
    </div>
    <slot />
  </div>
</template>

<style lang="scss" scoped>
.edit-field {
  cursor: default;

  // Inputs lisibles sur le fond sombre de l'outil : fond légèrement
  // éclairci, texte blanc franc, focus à la couleur de la carte — rendu
  // IDENTIQUE entre l'input du titre et le textarea de description. Element
  // Plus dessine ses bordures via box-shadow (y compris au hover/focus) : on
  // neutralise TOUS ces états, la vraie bordure fait foi.
  :deep(.el-input__wrapper),
  :deep(.el-input__wrapper:hover),
  :deep(.el-input__wrapper.is-focus),
  :deep(.el-textarea__inner),
  :deep(.el-textarea__inner:hover),
  :deep(.el-textarea__inner:focus) {
    box-shadow: none !important;
  }
  // Fond IDENTIQUE forcé : Element Plus pose son propre fond via des
  // variables CSS (--el-input-bg-color / fill-color) qui divergent entre
  // input et textarea sur le thème sombre — on court-circuite tout.
  :deep(.el-input__wrapper),
  :deep(.el-textarea__inner) {
    --el-input-bg-color: transparent;
    --el-fill-color-blank: transparent;
    background-color: rgba(255, 255, 255, 0.06) !important;
    border: 1px solid var(--canvas-border);
    border-radius: 6px;
    padding: 8px 10px;
  }
  :deep(.el-input__inner) {
    background: transparent !important;
  }
  :deep(.el-input__wrapper.is-focus),
  :deep(.el-input__wrapper:focus-within),
  :deep(.el-textarea__inner:focus) {
    border-color: var(--card-accent, var(--dr-report));
  }
  // Blanc de l'outil dessin, pas `--bs-white` : ce dernier est redéfini (et
  // devient sombre) dans un sous-arbre `data-bs-theme="dark"`, celui de la
  // carte d'observation.
  :deep(.el-input__inner),
  :deep(.el-textarea__inner) {
    color: var(--canvas-btn-color);
    font-size: 0.85rem;
    line-height: 1.4;

    &::placeholder {
      color: rgba(255, 255, 255, 0.35);
    }
  }
  :deep(.el-input__inner) {
    height: auto;
  }
}

// Sélecteur de partie : même habillage que les inputs Nom / Commentaire
// (son style propre suit les jetons Bootstrap, clairs ici).
.edit-field :deep(.part-line) {
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--canvas-border);
  border-radius: 6px;
  padding: 8px 10px;
  min-height: 0;
  color: var(--canvas-btn-color);

  &:hover {
    border-color: var(--card-accent, var(--dr-report));
  }
  .breadcrumb-path {
    font-size: 0.85rem;
    line-height: 1.4;
  }
  .breadcrumb-path .separator {
    color: rgba(255, 255, 255, 0.45);
  }
  .placeholder-text {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.35);
  }
  .text-secondary {
    color: rgba(255, 255, 255, 0.5) !important;
  }
}

.edit-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 3px;
}

.edit-label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  opacity: 0.7;
}
</style>
